import { apiClient, ensureCsrfCookie } from '@/api/client'
import type { ApiSuccessResponse, PaginationMeta } from '@/types/api'

export interface CandidateSearchResult {
  studentProfileId: number
  name: string
  // "Job Portal (native)" for a directly-registered candidate, or
  // "{lms name} · {academic phase}" for one sourced from an external
  // LMS placement form — see StudentProfile::programLabel(). Every
  // source+phase combination for the same real person is its own
  // separate profile/card, never merged.
  program: string
  headline: string | null
  currentCity: string | null
  currentState: string | null
  currentCompany: string | null
  currentDesignation: string | null
  totalExperienceMonths: number | null
  isFresher: boolean
  expectedCtc: number | null
  noticePeriodDays: number | null
  topSkills: string[]
  topEducations: string[]
  preferredLocations: string[]
  profileCompletionPercent: number
  hasResume: boolean
  updatedAt: string | null
  photoUrl: string | null
}

export interface AiRankedCandidate extends CandidateSearchResult {
  aiScore: number
  aiExplanation: string
}

// Candidate Search v2 — deliberately not AI, see CandidateSearchV2Controller.
// matchScore is pure OpenSearch keyword-relevance (0-100, normalized to the
// top result in the current search), not a Gemini call.
export interface KeywordMatchedCandidate extends CandidateSearchResult {
  matchScore: number
}

export interface CandidateProfile {
  studentProfileId: number
  name: string
  program: string
  email: string
  phone: string | null
  headline: string | null
  careerSummary: string | null
  currentCity: string | null
  currentState: string | null
  currentCountry: string | null
  preferredWorkMode: string | null
  willingToRelocate: boolean | null
  gender: string | null
  dob: string | null
  currentCompany: string | null
  currentDesignation: string | null
  totalExperienceMonths: number | null
  relevantExperienceMonths: number | null
  isFresher: boolean
  currentCtc: number | null
  currentCtcCurrency: string | null
  expectedCtc: number | null
  salaryCurrency: string | null
  noticePeriodDays: number | null
  isServingNoticePeriod: boolean | null
  profileCompletionPercent: number
  candidateCategory: string | null
  workPermitCountries: string[] | null
  hasCareerBreak: boolean | null
  careerBreakDurationMonths: number | null
  careerBreakReason: string | null
  isDifferentlyAbled: boolean | null
  disabilityTypes: string[] | null
  defenceBackground: string | null
  industries: string[]
  departmentTaxonomies: { id: number; name: string; breadcrumb: string }[]
  skills: { name: string; level: string | null; yearsOfExperience: number | null }[]
  educations: {
    level: string | null
    degree: string
    course: string | null
    specialization: string | null
    institutionName: string
    university: string | null
    startYear: number | null
    endYear: number | null
    percentage: number | null
  }[]
  experiences: {
    jobTitle: string
    companyName: string | null
    isCurrent: boolean
    startDate: string | null
    endDate: string | null
  }[]
  certifications: string[]
  languages: { name: string; proficiency: string | null }[]
  hasResume: boolean
  photoUrl: string | null
}

export type KeywordMode = 'include_and' | 'include_or' | 'mandatory' | 'exclude'
export type KeywordScope = 'title' | 'summary' | 'skills' | 'entire_resume'
export type EducationLevelMode = 'any' | 'specific' | 'none'
export type NoticePeriodBucket =
  | 'immediate'
  | '0_15_days'
  | '1_month'
  | '2_months'
  | '3_months'
  | 'more_than_3_months'

export interface KeywordChip {
  value: string
  mode: KeywordMode
}

export interface SkillChip {
  name: string
  min_years?: number
}

export interface EducationLevelFilterInput {
  mode: EducationLevelMode
  degree?: string
  specialization?: string
  university?: string
  year_from?: number
  year_to?: number
  percentage_min?: number
}

export interface CandidateSearchFilters {
  q?: string
  skills?: string[]
  education?: string[]
  current_location?: string
  // Multi-value variant used by Candidate Search v2's location chip
  // multiselect — current_location above stays for v1, unchanged.
  current_locations?: string[]
  preferred_locations?: string[]
  experience_min_months?: number
  experience_max_months?: number
  salary_min?: number
  salary_max?: number
  notice_period_max_days?: number
  languages?: string[]
  job_roles?: string[]
  employment_types?: string[]
  certifications?: string[]
  sort?: 'relevance' | 'experience_desc' | 'recent_update'
  page?: number
  per_page?: number

  // --- Candidate Search v2 additions below ---
  keywords?: KeywordChip[]
  keyword_scope?: KeywordScope
  skill_filters?: SkillChip[]
  notice_period_bucket?: NoticePeriodBucket
  is_serving_notice_period?: boolean
  current_company?: string
  previous_company?: string
  exclude_companies?: string[]
  current_designation?: string
  previous_designation?: string
  exclude_designations?: string[]
  industry_ids?: number[]
  department_taxonomy_ids?: number[]
  salary_currency?: string
  current_salary_min?: number
  current_salary_max?: number
  include_undisclosed_salary?: boolean
  willing_to_relocate?: boolean
  exclude_anywhere?: boolean
  education_ug?: EducationLevelFilterInput
  education_pg?: EducationLevelFilterInput
  education_doctorate?: EducationLevelFilterInput
  gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say'
  has_career_break?: boolean
  is_differently_abled?: boolean
  defence_background?: 'army' | 'navy' | 'air_force' | 'other_paramilitary'
  candidate_category?: 'fresher' | 'experienced' | 'intern' | 'freelancer' | 'returning_professional'
  age_min?: number
  age_max?: number
  work_permit_countries?: string[]
}

export interface AiCandidateSearchFilters extends Omit<CandidateSearchFilters, 'sort' | 'page' | 'per_page'> {
  requirement?: string
}

export interface CandidateFilterOptions {
  skills: string[]
  locations: string[]
  companies: string[]
  designations: string[]
  industries: { id: number; name: string }[]
  departmentTaxonomies: { id: number; parentId: number | null; name: string; breadcrumb: string }[]
  disabilityTypes: { id: number; name: string }[]
}

export interface RecentCandidateSearch {
  id: number
  summary: string
  filters: Record<string, unknown>
  resultsCount: number
  searchedAt: string
}

export interface CandidateSavedSearch {
  id: number
  name: string
  filters: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

export type CandidateTaskType = 'call_later' | 'interview_follow_up' | 'send_jd' | 'other'
export type CandidateTaskStatus = 'pending' | 'done'

export interface CandidateTask {
  id: number
  studentProfileId: number
  candidateName?: string
  type: CandidateTaskType
  note: string | null
  dueAt: string | null
  status: CandidateTaskStatus
  createdBy?: { id: number; name: string }
  assignedTo?: { id: number; name: string } | null
  createdAt: string
}

export interface CandidateTaskAssignee {
  id: number
  name: string
}

export const candidatesApi = {
  search: async (filters: CandidateSearchFilters) => {
    const { data } = await apiClient.get<
      ApiSuccessResponse<CandidateSearchResult[]> & { meta: PaginationMeta }
    >('/recruiter/candidates/search', { params: filters })
    return { results: data.data, meta: data.meta }
  },

  // Same filters as search(), but scored: each candidate comes back with an
  // explicit keyword-match percentage instead of only an implicit sort order.
  searchV2: async (filters: CandidateSearchFilters) => {
    const { data } = await apiClient.get<
      ApiSuccessResponse<KeywordMatchedCandidate[]> & { meta: PaginationMeta }
    >('/recruiter/candidates/search-v2', { params: filters })
    return { results: data.data, meta: data.meta }
  },

  get: async (studentProfileId: number) => {
    const { data } = await apiClient.get<ApiSuccessResponse<CandidateProfile>>(
      `/recruiter/candidates/${studentProfileId}`,
    )
    return data.data
  },

  // Ranks the same filter-narrowed pool /candidates/search would return
  // against a free-text requirement — never the whole candidate pool (see
  // AiCandidateSearchController). Kicks off chunked background scoring and
  // returns immediately; results stream in over the private user channel
  // (see useAiCandidateSearchStream), not in this response.
  aiSearch: async (filters: AiCandidateSearchFilters) => {
    await ensureCsrfCookie()
    const { data } = await apiClient.post<
      ApiSuccessResponse<{ searchRunId: string | null; filteredCount: number; totalChunks: number }>
    >('/recruiter/candidates/ai-search', filters)
    return data.data
  },

  filterOptions: async () => {
    const { data } = await apiClient.get<ApiSuccessResponse<CandidateFilterOptions>>(
      '/recruiter/candidates/filter-options',
    )
    return data.data
  },

  recentSearches: async () => {
    const { data } = await apiClient.get<ApiSuccessResponse<RecentCandidateSearch[]>>(
      '/recruiter/candidates/recent-searches',
    )
    return data.data
  },

  savedSearches: async () => {
    const { data } = await apiClient.get<ApiSuccessResponse<CandidateSavedSearch[]>>(
      '/recruiter/candidates/saved-searches',
    )
    return data.data
  },

  saveSearch: async (payload: { name: string; filters: CandidateSearchFilters }) => {
    await ensureCsrfCookie()
    const { data } = await apiClient.post<ApiSuccessResponse<CandidateSavedSearch>>(
      '/recruiter/candidates/saved-searches',
      payload,
    )
    return data.data
  },

  renameSavedSearch: async (id: number, name: string) => {
    await ensureCsrfCookie()
    const { data } = await apiClient.put<ApiSuccessResponse<CandidateSavedSearch>>(
      `/recruiter/candidates/saved-searches/${id}`,
      { name },
    )
    return data.data
  },

  deleteSavedSearch: async (id: number) => {
    await ensureCsrfCookie()
    await apiClient.delete(`/recruiter/candidates/saved-searches/${id}`)
  },

  taskAssignees: async () => {
    const { data } = await apiClient.get<ApiSuccessResponse<CandidateTaskAssignee[]>>(
      '/recruiter/candidates/task-assignees',
    )
    return data.data
  },

  myTasks: async (params?: { assigned_to_me?: boolean; status?: CandidateTaskStatus }) => {
    const { data } = await apiClient.get<ApiSuccessResponse<CandidateTask[]>>('/recruiter/tasks', { params })
    return data.data
  },

  createTask: async (
    studentProfileId: number,
    payload: { type: CandidateTaskType; note?: string; due_at?: string; assigned_to_user_id?: number },
  ) => {
    await ensureCsrfCookie()
    const { data } = await apiClient.post<ApiSuccessResponse<CandidateTask>>(
      `/recruiter/candidates/${studentProfileId}/tasks`,
      payload,
    )
    return data.data
  },

  updateTask: async (
    id: number,
    payload: Partial<{ note: string; due_at: string; status: CandidateTaskStatus; assigned_to_user_id: number }>,
  ) => {
    await ensureCsrfCookie()
    const { data } = await apiClient.put<ApiSuccessResponse<CandidateTask>>(`/recruiter/tasks/${id}`, payload)
    return data.data
  },

  deleteTask: async (id: number) => {
    await ensureCsrfCookie()
    await apiClient.delete(`/recruiter/tasks/${id}`)
  },
}
